/**
 * GitHub / Google sign-in helpers shared by the login and sign-up screens, the
 * router guard and the profile's "How you sign in" card.
 *
 * The API ends every social flow with a redirect back to the SPA carrying
 * `?social=<outcome>&provider=<provider>`: to `/login` after a sign-in or
 * sign-up, to `/profile` after a link. Both values come from the address bar,
 * so nothing here renders them as written: the provider is looked up in
 * PROVIDERS and the outcome in the message tables below, and anything unknown
 * falls back to a generic message.
 */

/** The providers the SPA knows how to draw a button for, in display order. */
export const SOCIAL_PROVIDERS = ['github', 'google']

const PROVIDER_LABELS = new Map([
    ['github', 'GitHub'],
    ['google', 'Google'],
])

/** Sign-in outcomes that leave a session cookie behind. */
export const SOCIAL_SESSION_OUTCOMES = ['signed_in', 'signed_up']

/**
 * @param {unknown} provider
 * @returns {boolean} Whether this is a provider the SPA has a button for
 */
export function isKnownSocialProvider(provider) {
    return SOCIAL_PROVIDERS.includes(provider)
}

/**
 * @param {unknown} provider
 * @returns {string} Display name, or a neutral phrase for one we don't know
 */
export function socialProviderLabel(provider) {
    return PROVIDER_LABELS.get(provider) ?? 'that provider'
}

/**
 * Keep only providers the SPA can draw, in display order, whatever order or
 * extras the API sends.
 *
 * @param {unknown} providers
 * @returns {string[]}
 */
export function knownSocialProviders(providers) {
    if (!Array.isArray(providers)) {
        return []
    }

    return SOCIAL_PROVIDERS.filter((provider) => providers.includes(provider))
}

/**
 * A query value as a single string. A repeated param arrives as an array, in
 * which case the first value wins.
 *
 * @param {unknown} value
 * @returns {string|null}
 */
function firstValue(value) {
    const first = Array.isArray(value) ? value[0] : value
    return typeof first === 'string' && first !== '' ? first : null
}

/**
 * Read the social outcome the API put on the return URL.
 *
 * @param {Record<string, unknown>} query - vue-router's `route.query`
 * @returns {{ outcome: string, provider: string|null }|null} Null when this
 *   isn't a return from a provider
 */
export function readSocialReturn(query) {
    const outcome = firstValue(query?.social)
    if (outcome === null) {
        return null
    }

    const provider = firstValue(query?.provider)

    return {
        outcome,
        provider: isKnownSocialProvider(provider) ? provider : null,
    }
}

/**
 * The query without the social-return params, so a refresh doesn't replay the
 * message, while anything else on the URL (`continue`, `t`) survives.
 *
 * @param {Record<string, unknown>} query - vue-router's `route.query`
 * @returns {Record<string, unknown>}
 */
export function withoutSocialReturn(query) {
    const { social: _social, provider: _provider, ...rest } = query ?? {}
    return rest
}

/**
 * Whether this outcome means the person is now signed in.
 *
 * @param {string|null|undefined} outcome
 * @returns {boolean}
 */
export function isSocialSessionOutcome(outcome) {
    return SOCIAL_SESSION_OUTCOMES.includes(outcome)
}

const SIGN_IN_MESSAGES = new Map(
    Object.entries({
        verification_required: (p) => ({
            type: 'warning',
            text: `That email already has a Zenmanage account, and ${p} isn't connected to it yet. Sign in the way you normally do, then connect ${p} from your profile.`,
        }),
        email_unverified: (p) => ({
            type: 'error',
            text: `${p} didn't give us a verified email address, so we couldn't sign you in. Verify your email with ${p} and try again, or use your email and password.`,
        }),
        denied: (p) => ({
            type: 'warning',
            text: `You cancelled the ${p} sign-in. Nothing was changed. Try again when you're ready.`,
        }),
        signup_disabled: () => ({
            type: 'error',
            text: "We aren't creating new accounts right now. If you already have an account, sign in with your email and password.",
        }),
        account_locked: () => ({
            type: 'error',
            text: 'This account is locked. Reset your password to unlock it, or contact support.',
        }),
        account_cancelled: () => ({
            type: 'error',
            text: 'This account has been cancelled. Contact support if you think that is a mistake.',
        }),
        account_suspended: () => ({
            type: 'error',
            text: 'This account is suspended. Contact support to get it back.',
        }),
        rate_limited: () => ({
            type: 'error',
            text: 'Too many sign-in attempts. Wait a minute and try again.',
        }),
        failed: (p) => ({
            type: 'error',
            text: `Something went wrong signing you in with ${p}. Try again, or use your email and password.`,
        }),
    })
)

const LINK_MESSAGES = new Map(
    Object.entries({
        linked: (p) => ({
            type: 'success',
            text: `${p} is connected. You can use it to sign in.`,
        }),
        already_linked: (p) => ({
            type: 'success',
            text: `${p} was already connected to your account.`,
        }),
        identity_in_use: (p) => ({
            type: 'error',
            text: `That ${p} account is already connected to a different Zenmanage user, so we couldn't connect it to yours.`,
        }),
        provider_already_linked: (p) => ({
            type: 'error',
            text: `You've already connected a different ${p} account. Disconnect it first, then connect the new one.`,
        }),
        denied: (p) => ({
            type: 'warning',
            text: `You cancelled connecting ${p}. Nothing was changed.`,
        }),
        email_unverified: (p) => ({
            type: 'error',
            text: `${p} didn't give us a verified email address, so we couldn't connect it. Verify your email with ${p} and try again.`,
        }),
        rate_limited: () => ({
            type: 'error',
            text: 'Too many attempts. Wait a minute and try again.',
        }),
        failed: (p) => ({
            type: 'error',
            text: `Something went wrong connecting ${p}. Try again.`,
        }),
    })
)

function messageFrom(table, outcome, provider) {
    const build = table.get(outcome) ?? table.get('failed')
    return build(socialProviderLabel(provider))
}

/**
 * What to tell someone who came back to the login screen from a provider.
 * Null when the outcome is a successful sign-in: they don't get a message,
 * they get the app.
 *
 * @param {string} outcome
 * @param {string|null} provider
 * @returns {{ type: 'error'|'warning', text: string }|null}
 */
export function socialSignInMessage(outcome, provider) {
    if (isSocialSessionOutcome(outcome)) {
        return null
    }

    return messageFrom(SIGN_IN_MESSAGES, outcome, provider)
}

/**
 * What to tell someone who came back to their profile from connecting a
 * provider.
 *
 * @param {string} outcome
 * @param {string|null} provider
 * @returns {{ type: 'success'|'error'|'warning', text: string }}
 */
export function socialLinkMessage(outcome, provider) {
    return messageFrom(LINK_MESSAGES, outcome, provider)
}
